import React, { useState, useRef, useEffect, useCallback, useMemo, useContext } from 'react';
import { db } from "../../firebase";
import { collection, getDocs, doc, setDoc, updateDoc, arrayUnion, Timestamp, query, where, orderBy, addDoc, onSnapshot, getFirestore } from "firebase/firestore";
import { LoadScript, GoogleMap, Marker, InfoWindow, Polyline, OverlayView, TrafficLayer } from '@react-google-maps/api';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import hydrantIcon from "../../Assets/hydrant.png"; 
import engineIcon from "../../Assets/fire-truck.png"; 
import chiefIcon from "../../Assets/default.png"; 
import ambulanceIcon from "../../Assets/ambulance.png"; 
import responderIcon from "../../Assets/default.png";  
import firePin from "../../Assets/firepin.png"; 
import emsPin from "../../Assets/emspin.png"; 
import markerImage from "../../Assets/call.png";
import medevacIcon from "../../Assets/medevac.gif";
import hydrantdata from "../../Assets/suffolkhydrants.json"; 
import { getDatabase, ref, onValue } from "firebase/database";
import { AuthContext } from "../../context/AuthContext";
import moment from 'moment';
import { useLocation, useSearchParams } from 'react-router-dom';
import "./GoogleMapComponent.scss";

const containerStyle = {
  width: '100vw',
  height: '100vh',
};

const defaultCenter = {
  lat: 40.8228121,
  lng: -73.1496657
};

const darkModeStyle = [
  {
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#212121"
      }
    ]
  },
  {
    "elementType": "labels.icon",
    "stylers": [
      {
        "visibility": "off"
      }
    ]
  },
  {
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#757575"
      }
    ]
  },
  {
    "elementType": "labels.text.stroke",
    "stylers": [
      {
        "color": "#212121"
      }
    ]
  },
  {
    "featureType": "administrative",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#757575"
      }
    ]
  },
  {
    "featureType": "administrative.country",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#9e9e9e"
      }
    ]
  },
  {
    "featureType": "administrative.land_parcel",
    "stylers": [
      {
        "visibility": "off"
      }
    ]
  },
  {
    "featureType": "administrative.locality",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#bdbdbd"
      }
    ]
  },
  {
    "featureType": "poi",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#757575"
      }
    ]
  },
  {
    "featureType": "poi.park",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#181818"
      }
    ]
  },
  {
    "featureType": "poi.park",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#616161"
      }
    ]
  },
  {
    "featureType": "poi.park",
    "elementType": "labels.text.stroke",
    "stylers": [
      {
        "color": "#1b1b1b"
      }
    ]
  },
  {
    "featureType": "road",
    "elementType": "geometry.fill",
    "stylers": [
      {
        "color": "#2c2c2c"
      }
    ]
  },
  {
    "featureType": "road",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#8a8a8a"
      }
    ]
  },
  {
    "featureType": "road.arterial",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#373737"
      }
    ]
  },
  {
    "featureType": "road.highway",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#3c3c3c"
      }
    ]
  },
  {
    "featureType": "road.highway.controlled_access",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#4e4e4e"
      }
    ]
  },
  {
    "featureType": "road.local",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#616161"
      }
    ]
  },
  {
    "featureType": "transit",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#757575"
      }
    ]
  },
  {
    "featureType": "water",
    "elementType": "geometry",
    "stylers": [
      {
        "color": "#000000"
      }
    ]
  },
  {
    "featureType": "water",
    "elementType": "labels.text.fill",
    "stylers": [
      {
        "color": "#3d3d3d"
      }
    ]
  }
];

const FullScreenMap = () => {
    const { userData } = useContext(AuthContext);
    const [searchParams] = useSearchParams();
    const ADSB_PROXY_SERVER = 'http://ladderradar.ddns.net:5863';
    
    const [mapRegion, setMapRegion] = useState({
      lat: defaultCenter.lat,
      lng: defaultCenter.lng,
      latitudeDelta: 0.01,
      longitudeDelta: 0.01,
    });
    const [hydrantLayerVisible, setHydrantLayerVisible] = useState(true);
    const [hydrantLayerAutoDisabled, setHydrantLayerAutoDisabled] = useState(false);
    // Track manual toggle state separately from auto-disable
    const [userToggledHydrants, setUserToggledHydrants] = useState(false);
    const [trafficLayerVisible, setTrafficLayerVisible] = useState(true); // State for traffic layer visibility
    const [airTrafficVisible, setAirTrafficVisible] = useState(false); // State for air traffic layer visibility
    const [showMedevacOnly, setShowMedevacOnly] = useState(false); // State for MEDEVAC-only filter
    // These state variables are kept for future UI enhancements but currently not displayed
    // const [lastAirTrafficUpdate, setLastAirTrafficUpdate] = useState(null);
    // const [airTrafficError, setAirTrafficError] = useState(false);
    // const [airTrafficErrorMessage, setAirTrafficErrorMessage] = useState('');
    // const [nextAirTrafficRetry, setNextAirTrafficRetry] = useState(null);
    
    // Using these setters but commenting out the unused state variables
    const setLastAirTrafficUpdate = (value) => {};
    const setAirTrafficError = (value) => {};
    const setAirTrafficErrorMessage = (value) => {};
    const setNextAirTrafficRetry = (value) => {};
    const [aircraft, setAircraft] = useState([]);
    const [filteredHydrants, setFilteredHydrants] = useState([]);
    const [selectedHydrant, setSelectedHydrant] = useState(null);
    const [selectedVehicle, setSelectedVehicle] = useState(null); 
    const [marineTrafficVisible, setMarineTrafficVisible] = useState(false);
    const [marineTrafficError, setMarineTrafficError] = useState(false);
    const [hydrants, setHydrants] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [vessels, setVessels] = useState([]);
    const [alarms, setAlarms] = useState([]);
    const [selectedAlarm, setSelectedAlarm] = useState(null);
    const [darkMode, setDarkMode] = useState(true); // State for dark mode
    const [callerLocation, setCallerLocation] = useState(null);
    const mapRef = useRef(null);
    // const airTrafficOverlayRef = useRef(null); // Not currently used but may be needed for future enhancements
    
    const retryCountRef = useRef(0);
    const lastFetchTimestampRef = useRef(0);
    const [lastMarineTrafficUpdate, setLastMarineTrafficUpdate] = useState(
          new Date().toISOString()
        );

     // Single NOAA Weather radar source
        const radarUrlTemplate = useMemo(
          () =>
            "https://opengeo.ncep.noaa.gov/geoserver/conus/conus_bref_qcd/ows?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetMap&FORMAT=image/png&TRANSPARENT=true&LAYERS=conus_bref_qcd&STYLES=&WIDTH=256&HEIGHT=256&CRS=EPSG:3857&BBOX={bbox}",
          []
        );
      
        // Fallback marine data if primary URL fails
        const marineTrafficFallbackUrl = useMemo(
          () =>
            "https://coast.noaa.gov/arcgis/services/MarineCadastre/AIS/MapServer/WMSServer?REQUEST=GetMap&SERVICE=WMS&VERSION=1.3.0&LAYERS=0&STYLES=&FORMAT=image/png&TRANSPARENT=TRUE&CRS=EPSG:3857&BBOX={bbox}&WIDTH=256&HEIGHT=256",
          []
        );

        
  
    useEffect(() => {
      console.log('Loading hydrant data', hydrantdata?.length || 0, 'hydrants');
      setHydrants(hydrantdata);
      
      // Initialize filtered hydrants with all hydrants
      if (hydrantdata?.length > 0) {
        setFilteredHydrants(hydrantdata);
      }
  
      // Function to load units from a single agency
      const loadUnitsFromAgency = (agencyId) => {
        console.log(`Loading units from agency: ${agencyId}`);
        const unitsRef = ref(getDatabase(), `/UnitLocation/${agencyId}`);
        
        return onValue(unitsRef, (snapshot) => {
          const unitsData = snapshot.val();
          if (unitsData) {
            // Calculate the timestamp for 24 hours ago
            const oneDayAgo = new Date();
            oneDayAgo.setHours(oneDayAgo.getHours() - 24);
            const oneDayAgoTimestamp = oneDayAgo.getTime();
            
            // Create unit array and filter by timestamp
            const unitArray = Object.keys(unitsData)
              .map(key => ({
                responderName: key,
                agency: agencyId, // Add agency ID to track which agency each unit belongs to
                ...unitsData[key],
              }))
              .filter(unit => {
                // Check if the unit has a valid timestamp
                const unitTimestamp = unit.timestamp || 0;
                
                // Keep only units updated within the last 24 hours
                const isRecent = unitTimestamp >= oneDayAgoTimestamp;
                
                if (!isRecent) {
                  console.log(`Filtering out stale unit: ${unit.responderName} (last updated: ${new Date(unitTimestamp).toLocaleString()})`);
                }
                
                return isRecent;
              });
            
            console.log(`Agency ${agencyId}: Found ${Object.keys(unitsData).length} units, ${unitArray.length} are active within last 24h`);
            
            // Merge with existing units
            setVehicles(prevVehicles => {
              // Filter out any previous units from this agency to avoid duplicates
              const filteredPrevVehicles = prevVehicles.filter(v => v.agency !== agencyId);
              return [...filteredPrevVehicles, ...unitArray];
            });
          }
        });
      };
      
      // Track all database listeners to unsubscribe later
      const listeners = [];
      
      // Always load units from the primary agency
      if (userData?.agency) {
        listeners.push(loadUnitsFromAgency(userData.agency));
        
        // Also load units from any associated agencies
        if (userData.associatedAgencies && Array.isArray(userData.associatedAgencies)) {
          console.log(`Found ${userData.associatedAgencies.length} associated agencies`);
          
          userData.associatedAgencies.forEach(associatedAgency => {
            if (associatedAgency && associatedAgency !== userData.agency) {
              listeners.push(loadUnitsFromAgency(associatedAgency));
            }
          });
        }
      }
      
      // Return cleanup function to unsubscribe from all listeners
      return () => {
        listeners.forEach(unsubscribe => {
          if (typeof unsubscribe === 'function') {
            unsubscribe();
          }
        });
      };
    }, [userData]);
  
    const fetchAlarms = useCallback(() => {
      if (userData?.agency) {
        const currentTime = new Date();
        const pastTime = new Date(currentTime.getTime() - 24 * 60 * 60 * 1000); // 24 hours ago
        console.log("Fetching alarms from:", pastTime);
    
        const q = query(
          collection(getFirestore(), `${userData.agency} Alarms`),
          where("TimeStamp", ">=", Timestamp.fromDate(pastTime))
        );
    
        const unsubscribe = onSnapshot(q, async (querySnapshot) => {
          console.log("Query snapshot size:", querySnapshot.size);
          const alarms = await Promise.all(
            querySnapshot.docs.map(async (doc) => {
              const data = doc.data();
              if (!data.RunNumber) return null; // Filter out documents that don't have "RunNumber"
              
              let location;
              if (data.Latitude && data.Longitude) {
                location = {
                  lat: parseFloat(data.Latitude),
                  lng: parseFloat(data.Longitude)
                };
              } else {
                location = await geocodeAddress(data.Address, data.Town);
              }

              // Use Eventtime if TimeStamp is null
              const timeStamp = data.TimeStamp
                ? data.TimeStamp.toDate()
                : moment(
                    data.Eventtime.split("Dispatch Time: ")[1],
                    "MM-DD-YYYY HH:mm:ss"
                  ).toDate();
    
              return { ...data, location, id: doc.id, timeStamp };
            })
          );
    
          const nonNullAlarms = alarms.filter((alarm) => 
            alarm !== null && alarm.AlarmStatus !== "false"
          );
          nonNullAlarms.sort((a, b) => b.timeStamp - a.timeStamp);
    
          setAlarms(nonNullAlarms);
          console.log("Fetched alarms with locations:", nonNullAlarms);
        });
    
        return () => unsubscribe();
      }
    }, [userData]);
  
  
    const geocodeAddress = async (address, town) => {
      const apiKey = "AIzaSyDEYcdHgZwlpq3QxIRqtT8G3NmodCSwhiM";
      const fullAddress = `${address}, ${town}`;
      const response = await axios.get(
        `https://maps.googleapis.com/maps/api/geocode/json`,
        {
          params: {
            address: fullAddress,
            key: apiKey,
          },
        }
      );
    
      if (response.data.status === "OK") {
        const location = response.data.results[0].geometry.location;
        return location;
      } else {
        console.error("Geocoding error:", response.data.status);
        return null;
      }
    };
  
    useEffect(() => {
      fetchAlarms();
    }, [fetchAlarms]);
  
    const extractCallerCoordinates = useCallback((eventComments) => {
      console.log('Extracting caller coordinates from Event Comments');
      
      // Define a regular expression pattern to match coordinates
      const regex = /-?\d+\.\d+\s+-?\d+\.\d+/;
      
      if (!eventComments) {
        console.log('No Event Comments available.');
        return;
      }

      // Find matches in the EventComments text
      const matches = eventComments.match(regex);
      
      if (matches && matches.length > 0) {
        // The first match contains the coordinates
        const coordinates = matches[0];
        console.log('Extracted Coordinates:', coordinates);
      
        // Parse the coordinates
        const [longitudeStr, latitudeStr] = coordinates.split(' ');
        const latitude = parseFloat(latitudeStr);
        const longitude = parseFloat(longitudeStr);
        
        console.log('Parsed Latitude:', latitude);
        console.log('Parsed Longitude:', longitude);
        console.log('Setting callerLocation state...');
      
        if (!isNaN(latitude) && !isNaN(longitude)) {
          // Save the coordinates in state
          setCallerLocation({ latitude, longitude });
        } else {
          console.log('Invalid coordinates extracted.');
        }
      } else {
        console.log('No coordinates found in Event Comments.');
      }
    }, []);

    const handleAlarmClick = useCallback((alarm) => {
      if (alarm?.location?.lat && alarm?.location?.lng) {
        setMapRegion({
          lat: alarm.location.lat,
          lng: alarm.location.lng,
          latitudeDelta: 0.01,
          longitudeDelta: 0.01,
        });
      }
      // Extract caller coordinates when an alarm is selected
      if (alarm?.EventComments) {
        extractCallerCoordinates(alarm.EventComments);
      }
    }, [extractCallerCoordinates]);
  
    const mapCenter = defaultCenter;
  
    const mapOptions = {
      mapTypeId: darkMode ? 'roadmap' : 'hybrid', // Change map type based on dark mode
      disableDefaultUI: true, 
      zoomControl: true, 
      scaleControl: true, 
      streetViewControl: true,
      styles: darkMode ? darkModeStyle : [], // Apply dark mode styles when darkMode is true
    };
  
    // Function to check if a hydrant is within the current map region
    const isHydrantInRegion = useCallback((hydrant, region) => {
      // Safely check coordinates without requiring Google Maps API
      try {  
        // Parse hydrant coordinates
        const hydrantLat = parseFloat(hydrant.Shape_Y);
        const hydrantLng = parseFloat(hydrant.Shape_X);
        const { lat, lng, latitudeDelta, longitudeDelta } = region;
        
        // Define map boundaries
        const north = lat + latitudeDelta / 2;
        const south = lat - latitudeDelta / 2;
        const east = lng + longitudeDelta / 2;
        const west = lng - longitudeDelta / 2;
        
        // Check if hydrant is within these boundaries
        return (
          hydrantLng >= west && 
          hydrantLng <= east && 
          hydrantLat >= south && 
          hydrantLat <= north
        );
      } catch (error) {
        console.error('Error in isHydrantInRegion:', error);
        return false;
      }
    }, []);
  
    // Region-based hydrant filtering to auto-disable with too many hydrants
    useEffect(() => {
      // Prevent operations if component is unmounting or prerequisites aren't met
      if (hydrants.length === 0 || !mapRegion) return;
      
      // Check if Google Maps is loaded safely
      if (typeof window.google === 'undefined') {
        console.log('Google Maps not yet loaded, skipping hydrant filtering');
        return;
      }

      let isMounted = true; // Flag to track if component is still mounted

      const filterHydrants = () => {
        // Safety check for unmount during async operations
        if (!isMounted) return;
        
        try {
          // Count how many hydrants would be in the current view
          const hydrantsInView = hydrants.filter(hydrant => 
            isHydrantInRegion(hydrant, mapRegion)
          );
          
          const inViewCount = hydrantsInView.length;
          
          // Take all hydrants for the filtered set but limit displayed ones
          const filtered = hydrantsInView.slice(0, 100);
          
          // Only update state if component is still mounted
          if (isMounted) {
            setFilteredHydrants(filtered);
            
            // Auto-manage layer visibility if user hasn't manually toggled
            if (!userToggledHydrants) {
              if (inViewCount > 100) {
                // Auto-disable if more than 100 hydrants would be visible
                setHydrantLayerVisible(false);
                setHydrantLayerAutoDisabled(true);
              } else if (inViewCount <= 100 && hydrantLayerAutoDisabled) {
                // Re-enable when fewer than or equal to 100 hydrants
                setHydrantLayerVisible(true);
                setHydrantLayerAutoDisabled(false);
              }
            }
          }
        } catch (error) {
          console.error('Error in hydrant filtering:', error);
        }
      };

      // Use requestAnimationFrame to ensure we don't attempt updates during render
      const rafId = requestAnimationFrame(filterHydrants);

      // Cleanup function to prevent memory leaks
      return () => {
        isMounted = false;
        cancelAnimationFrame(rafId);
      };
    }, [hydrants, mapRegion, isHydrantInRegion, hydrantLayerAutoDisabled, userToggledHydrants]);
  
    useEffect(() => {
      const alarmId = searchParams.get('alarmId');
      if (alarmId && alarms.length > 0) {
        const alarm = alarms.find(a => a.id === alarmId);
        if (alarm) {
          if (alarm?.location?.lat && alarm?.location?.lng) {
            // Center the map on the alarm location
            if (mapRef.current) {
              mapRef.current.panTo({ lat: alarm.location.lat, lng: alarm.location.lng });
              mapRef.current.setZoom(17);
            }
            setMapRegion({
              lat: alarm.location.lat,
              lng: alarm.location.lng,
              latitudeDelta: 0.01,
              longitudeDelta: 0.01,
            });
          }
          if (alarm?.EventComments) {
            extractCallerCoordinates(alarm.EventComments);
          }
        }
      }
    }, [searchParams, alarms, extractCallerCoordinates]);

    useEffect(() => {
      if (userData?.agency && userData?.id) {
        const selectedAlarmRef = ref(getDatabase(), `/PSAPLink/${userData.id}/SelectedAlarm/${userData.agency}`);
        const unsubscribe = onValue(selectedAlarmRef, (snapshot) => {
          const data = snapshot.val();
          if (data?.alarmId && alarms.length > 0) {
            const alarm = alarms.find(a => a.id === data.alarmId);
            if (alarm) {
              if (alarm?.location?.lat && alarm?.location?.lng) {
                // Center the map on the alarm location
                if (mapRef.current) {
                  mapRef.current.panTo({ lat: alarm.location.lat, lng: alarm.location.lng });
                  mapRef.current.setZoom(17);
                }
                setMapRegion({
                  lat: alarm.location.lat,
                  lng: alarm.location.lng,
                  latitudeDelta: 0.01,
                  longitudeDelta: 0.01,
                });
              }
              if (alarm?.EventComments) {
                extractCallerCoordinates(alarm.EventComments);
              }
            }
          }
        });

        return () => unsubscribe();
      }
    }, [userData, alarms, extractCallerCoordinates]);

    // List of MEDEVAC aircraft registrations to filter for
    const medevacRegistrations = useMemo(() => [
      'N902LC', 'N911SD', 'N911HD', 'N135NH', 'N901RX', 
      'N901NY', 'N902VY', 'N902NY', 'N86MD', 'N911LF',
      'N171PD', 'N352PD', 'N354PD', 'N3PD', 'N454PD', 
      'N83070', 'N994PD', 'N347MT', 'N21394', 'N828Y',
      'N605PD', 'N606PD', 'N604PD', 'N917PD', 'N918PD',
      'N919PD', 'N920PD'
    ], []);

    useEffect(() => {
      if (!airTrafficVisible && !showMedevacOnly) return;

      let isMounted = true;
      const maxRetryCount = 5;
      const baseRetryDelay = 30000; // Start with 30 seconds
      const minFetchInterval = 15000; // Minimum 15 seconds between API calls

      const calculateBackoff = (retry) => {
        const jitter = Math.random() * 5000;
        return Math.min(baseRetryDelay * Math.pow(2, retry) + jitter, 15 * 60 * 1000); // Max 15 minutes
      };

      const fetchAircraft = async () => {
        // Check if we've fetched recently - prevent excessive calls
        const now = Date.now();
        const timeElapsedSinceLastFetch = now - lastFetchTimestampRef.current;
        
        if (timeElapsedSinceLastFetch < minFetchInterval) {
          console.log(`Skipping aircraft fetch, last fetch was ${Math.round(timeElapsedSinceLastFetch/1000)} seconds ago`);
          return;
        }
        
        try {
          // Clear previous error state
          setAirTrafficError(false);
          setAirTrafficErrorMessage('');
          
          console.log("Fetching aircraft data from ADS-B.fi API");
          
          if (!mapRef.current) {
            console.log("Map reference not available");
            return;
          }
          
          // Get current map bounds
          const bounds = mapRef.current.getBounds();
          if (!bounds) {
            console.log("Map bounds not available");
            return;
          }

          const center = mapRef.current.getCenter();
          const latitude = center.lat();
          const longitude = center.lng();
          
          // Calculate the approximate distance in nautical miles based on the map bounds
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          const latDiff = Math.abs(ne.lat() - sw.lat());
          const lngDiff = Math.abs(ne.lng() - sw.lng());
          
          // Convert the delta to approximate NM (1 degree latitude ≈ 60 NM)
          // Make sure we have a minimum search radius of 25 NM to capture nearby aircraft
          const calculatedDistNM = Math.ceil(Math.max(latDiff, lngDiff) * 60);
          const distanceNM = Math.min(Math.max(calculatedDistNM, 25), 250);

          try {
            // Use the ADSB proxy server
            const url = `${ADSB_PROXY_SERVER}/api/adsb/${latitude}/${longitude}/${distanceNM}`;
            console.log("Fetching aircraft from:", url);
            
            const response = await axios.get(url);
            
            if (response.status !== 200) {
              throw new Error(`API returned status ${response.status}`);
            }
            
            const data = response.data;
            
            console.log(`API response structure: ${JSON.stringify(Object.keys(data))}`);
            console.log(`Aircraft count in response: ${data.aircraft?.length || 0}`);
            
            if (isMounted && data && data.aircraft && data.aircraft.length > 0) {
              // Process aircraft data
              const processedAircraft = data.aircraft.map(aircraft => ({
                icao24: aircraft.hex,                   // ICAO 24-bit address
                callsign: aircraft.flight?.trim() || 'Unknown',  // Callsign
                country: aircraft.cou || 'Unknown',     // Country of origin
                longitude: aircraft.lon,                // Longitude
                latitude: aircraft.lat,                 // Latitude
                altitude: aircraft.alt_baro || aircraft.alt_geom || 0,  // Altitude (prefer barometric)
                onGround: aircraft.alt_baro === 0 || (aircraft.alt_baro === undefined && aircraft.alt_geom === 0),  // On ground status
                velocity: aircraft.gs || 0,             // Ground speed in knots
                heading: aircraft.track || 0,           // True track in decimal degrees (0-360)
                verticalRate: aircraft.baro_rate || 0,  // Vertical rate in feet/min
                squawk: aircraft.squawk,                // Transponder code
                aircraft_type: aircraft.t,              // Aircraft type
                registration: aircraft.r,               // Registration
                lastUpdate: new Date(aircraft.seen * 1000)  // Last seen timestamp
              }));
              
              // Limit maximum displayed aircraft to 25 to reduce lag
              // Prioritize MEDEVAC aircraft first, then sort others by closest to center of map view
              let limitedAircraft = processedAircraft;
              
              // Check if we need to limit the number of aircraft
              if (processedAircraft.length > 25) {
                console.log(`Limiting aircraft display from ${processedAircraft.length} aircraft`);
                
                // Identify MEDEVAC aircraft
                const medevacAircraft = processedAircraft.filter(plane => 
                  plane.registration && 
                  medevacRegistrations.some(reg => 
                    plane.registration.toUpperCase().includes(reg.toUpperCase())
                  )
                );
                
                // Get non-MEDEVAC aircraft
                const regularAircraft = processedAircraft.filter(plane => 
                  !plane.registration || 
                  !medevacRegistrations.some(reg => 
                    plane.registration.toUpperCase().includes(reg.toUpperCase())
                  )
                );
                
                // Sort regular aircraft by distance from map center
                regularAircraft.sort((a, b) => {
                  const distA = Math.sqrt(
                    Math.pow(a.latitude - latitude, 2) + 
                    Math.pow(a.longitude - longitude, 2)
                  );
                  const distB = Math.sqrt(
                    Math.pow(b.latitude - latitude, 2) + 
                    Math.pow(b.longitude - longitude, 2)
                  );
                  return distA - distB;
                });
                
                // Combine MEDEVAC aircraft with closest regular aircraft, up to 25 total
                limitedAircraft = [
                  ...medevacAircraft,
                  ...regularAircraft.slice(0, Math.max(0, 25 - medevacAircraft.length))
                ];
              }
              
              setAircraft(limitedAircraft);
              setLastAirTrafficUpdate(new Date().toISOString());
              console.log(`Processed ${processedAircraft.length} aircraft, displaying ${limitedAircraft.length}`);
              
              // Update timestamp and reset retry counter
              lastFetchTimestampRef.current = now;
              retryCountRef.current = 0;
              setNextAirTrafficRetry(null);
            } else {
              console.warn("No aircraft data received from API");
              setAircraft([]);
            }
          } catch (error) {
            console.error("Error fetching aircraft data:", error);
            
            setAirTrafficError(true);
            setAirTrafficErrorMessage(error.message || 'Unknown error fetching aircraft data');
            
            if (retryCountRef.current < maxRetryCount) {
              retryCountRef.current++;
              const backoffDelay = calculateBackoff(retryCountRef.current - 1);
              const retryTime = new Date(Date.now() + backoffDelay);
              setNextAirTrafficRetry(retryTime.toISOString());
              console.log(`Retrying aircraft data in ${Math.round(backoffDelay/1000)} seconds (retry ${retryCountRef.current})`);
              
              setTimeout(() => {
                if (isMounted) fetchAircraft();
              }, backoffDelay);
            } else {
              console.log("Max retry count reached for aircraft data");
            }
          }
        } catch (error) {
          console.error("Error fetching aircraft data:", error);
          
          setAirTrafficError(true);
          setAirTrafficErrorMessage(error.message || 'Unknown error fetching aircraft data');
          
          if (retryCountRef.current < maxRetryCount) {
            retryCountRef.current++;
            const backoffDelay = calculateBackoff(retryCountRef.current - 1);
            const retryTime = new Date(Date.now() + backoffDelay);
            setNextAirTrafficRetry(retryTime.toISOString());
            console.log(`Retrying aircraft data in ${Math.round(backoffDelay/1000)} seconds (retry ${retryCountRef.current})`);
            
            setTimeout(() => {
              if (isMounted) fetchAircraft();
            }, backoffDelay);
          } else {
            console.log("Max retry count reached for aircraft data");
          }
        }
      };

      // Initial fetch
      fetchAircraft();

      // Set up periodic refresh
      const intervalId = setInterval(() => {
        if (isMounted) {
          fetchAircraft();
        }
      }, 30000); // Fetch every 30 seconds

      return () => {
        isMounted = false;
        clearInterval(intervalId);
      };
    }, [airTrafficVisible, showMedevacOnly, medevacRegistrations]);

    const handleRegionChangeComplete = useCallback(() => {
      if (mapRef.current && window.google) {
        const map = mapRef.current;
        const bounds = map.getBounds();
        if (bounds) {
          const center = map.getCenter();
          setMapRegion({
            lat: center.lat(),
            lng: center.lng(),
            latitudeDelta: bounds.toSpan().lat(),
            longitudeDelta: bounds.toSpan().lng(),
          });
        }
      }
    }, []);
  
    const handleLoad = useCallback((map) => {
      mapRef.current = map;
      handleRegionChangeComplete();
    }, [handleRegionChangeComplete]);
  
    const handleUnmount = useCallback(() => {
      mapRef.current = null;
    }, []);
  
    const getVehicleIcon = (vehicleType) => {
      switch ((vehicleType || "").toLowerCase()) {
        case "engine":
          return engineIcon;
        case "chief":
          return chiefIcon;
        case "ambulance":
          return ambulanceIcon;
        case "responder":
          return responderIcon;
        case "medevac":
          return medevacIcon;
        default:
          return responderIcon; 
      }
    };
  
    const getMarkerImage = (alarm) => {
      if (alarm.callcard === 'FIRE') {
        return firePin;
      } else if (alarm.callcard === 'EMS') {
        return emsPin;
      } else {
        return markerImage; // Use the default marker for other cases
      }
    };
  
    const toggleDarkMode = () => {
      setDarkMode((prevMode) => !prevMode);
    };
  
    const toggleHydrantLayer = () => {
      setHydrantLayerVisible((prevVisibility) => !prevVisibility);
      // Mark that user has manually toggled the layer
      setUserToggledHydrants(true);
      // Reset auto-disabled flag when manually toggled
      setHydrantLayerAutoDisabled(false);
    };
  
    const toggleTrafficLayer = () => {
      setTrafficLayerVisible((prevVisibility) => !prevVisibility);
    };

    const toggleMarineTrafficLayer = () => {
      setMarineTrafficVisible((prevVisibility) => !prevVisibility);
    };
  
    const toggleAirTrafficLayer = () => {
      if (!airTrafficVisible && !showMedevacOnly) {
        // First press: Show only MEDEVAC aircraft
        setAirTrafficVisible(false);
        setShowMedevacOnly(true);
      } else if (!airTrafficVisible && showMedevacOnly) {
        // Second press: Show all aircraft
        setAirTrafficVisible(true);
        setShowMedevacOnly(false);
      } else {
        // Third press: Hide all aircraft
        setAirTrafficVisible(false);
        setShowMedevacOnly(false);
      }
    };

    // Function to determine aircraft color based on altitude
    const getAircraftColor = useCallback((altitude) => {
      // Convert to number if it's a string and handle undefined
      const altitudeFt =
        typeof altitude === "string"
          ? parseInt(altitude, 10)
          : (altitude || 0);
      
      if (altitudeFt < 1000) {
        return '#ff0000'; // Red for very low altitude
      } else if (altitudeFt < 5000) {
        return '#ff9900'; // Orange for low altitude
      } else if (altitudeFt < 15000) {
        return '#ffcc00'; // Yellow for medium-low altitude
      } else if (altitudeFt < 25000) {
        return '#33cc33'; // Green for medium altitude
      } else if (altitudeFt < 35000) {
        return '#3399ff'; // Light blue for medium-high altitude
      } else {
        return '#0066cc'; // Dark blue for high altitude
      }
    }, []);

    useEffect(() => {
      if (!marineTrafficVisible) return;
  
      let isMounted = true;
      const fetchVessels = async () => {
        try {
          setMarineTrafficError(false);
          console.log("Fetching vessel data from API");
  
          // Use a public CORS proxy to bypass CORS restrictions
          // Note: This is a temporary solution - consider setting up your own proxy for production
          const CORS_PROXY = 'https://cors-anywhere.herokuapp.com/';
          const MARINE_API_URL = 'http://ladderems.ddns.net:5862/api/vessels';
          const apiUrl = `${CORS_PROXY}${MARINE_API_URL}`;
  
          console.log(`Using CORS Proxy URL: ${apiUrl}`);
  
          const response = await axios.get(apiUrl);
          // With axios, we check response.status
          if (response.status !== 200) {
            throw new Error(`API returned status ${response.status}`);
          }
  
          // Axios automatically parses JSON data into response.data
          const data = response.data;
  
          // Log the response structure to help debug
          console.log("Vessel API response:", data);
  
          if (isMounted) {
            // Process the vessel data according to the expected format
            if (data.vessels && Array.isArray(data.vessels)) {
              // Convert the API data format to our app's vessel format
              const processedVessels = data.vessels.map((vessel) => ({
                id: `vessel-${vessel.mmsi}`,
                mmsi: vessel.mmsi,
                name: vessel.shipName || `Vessel ${vessel.mmsi}`,
                latitude: vessel.latitude,
                longitude: vessel.longitude,
                heading: vessel.heading || vessel.course || 0,
                speed: vessel.speed || 0,
                course: vessel.course || 0,
                navStatus: vessel.navigationStatus,
                navStatusText: vessel.navigationStatusText,
                lastUpdate: vessel.lastUpdate,
                type: vessel.shipType || "Unknown",
                callSign: vessel.callSign,
                destination: vessel.destination,
              }));
  
              setVessels(processedVessels);
              setLastMarineTrafficUpdate(new Date().toISOString());
              console.log(`Processed ${processedVessels.length} vessels`);
            } else if (Array.isArray(data)) {
              // Handle case where API directly returns an array
              const processedVessels = data.map((vessel) => ({
                id: `vessel-${
                  vessel.mmsi || Math.random().toString(36).substr(2, 9)
                }`,
                mmsi: vessel.mmsi,
                name: vessel.shipName || vessel.name || `Vessel`,
                latitude: vessel.latitude,
                longitude: vessel.longitude,
                heading: vessel.heading || vessel.course || 0,
                speed: vessel.speed || 0,
                course: vessel.course || 0,
                navStatus: vessel.navigationStatus,
                navStatusText: vessel.navigationStatusText,
                lastUpdate: vessel.lastUpdate,
                type: vessel.shipType || vessel.type || "Unknown",
                callSign: vessel.callSign,
                destination: vessel.destination,
              }));
  
              setVessels(processedVessels);
              setLastMarineTrafficUpdate(new Date().toISOString());
              console.log(`Processed ${processedVessels.length} vessels`);
            } else {
              console.error("Invalid vessel data format:", data);
              setVessels([]);
            }
          }
        } catch (error) {
          console.error("Error fetching vessel data:", error);
          if (isMounted) {
            setMarineTrafficError(true);
          }
        }
      };
  
      // Fetch immediately
      fetchVessels();
  
      // Set up interval to refresh data
      const intervalId = setInterval(fetchVessels, 30000); // Refresh every 30 seconds
  
      return () => {
        isMounted = false;
        clearInterval(intervalId);
      };
    }, [marineTrafficVisible]);

    return (
      <div>
        <button 
          onClick={toggleDarkMode} 
          style={{ 
            position: 'absolute', 
            top: 10, 
            left: 10, 
            zIndex: 1000, 
            padding: '10px', 
            backgroundColor: darkMode ? '#ffffff' : '#333333', 
            color: darkMode ? '#333333' : '#ffffff',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          Toggle Dark Mode
        </button>
        <button 
          onClick={toggleHydrantLayer} 
          style={{ 
            position: 'absolute', 
            top: 60, 
            left: 10, 
            zIndex: 1000, 
            padding: '10px', 
            backgroundColor: hydrantLayerVisible ? '#ffffff' : '#333333', 
            color: hydrantLayerVisible ? '#333333' : '#ffffff',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          Toggle Hydrants
        </button>
        <button 
          onClick={toggleTrafficLayer} 
          style={{ 
            position: 'absolute', 
            top: 110, 
            left: 10, 
            zIndex: 1000, 
            padding: '10px', 
            backgroundColor: trafficLayerVisible ? '#ffffff' : '#333333', 
            color: trafficLayerVisible ? '#333333' : '#ffffff',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          Toggle Traffic
        </button>
        <button 
          onClick={toggleAirTrafficLayer} 
          style={{ 
            position: 'absolute', 
            top: 160, 
            left: 10, 
            zIndex: 1000, 
            padding: '10px', 
            backgroundColor: airTrafficVisible 
              ? '#0066cc' 
              : (showMedevacOnly ? '#ff0000' : '#333333'), 
            color: '#ffffff',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
          title={airTrafficVisible 
            ? 'Show All Aircraft' 
            : (showMedevacOnly ? 'Show MEDEVAC Only' : 'Hide Aircraft')}
        >
          Aircraft
        </button>
      
        <LoadScript 
          googleMapsApiKey="AIzaSyDEYcdHgZwlpq3QxIRqtT8G3NmodCSwhiM"
          onLoad={() => console.log('Script loaded successfully')}
          onError={(error) => console.error('Script loading error: ', error)}
        >
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={mapCenter}
            zoom={17}
            options={mapOptions}
            onLoad={handleLoad}
            onUnmount={handleUnmount}
            onIdle={handleRegionChangeComplete}
          >
            {mapCenter && <Marker position={mapCenter} />}
            {hydrantLayerVisible && typeof window.google !== 'undefined' && filteredHydrants.map((hydrant, index) => (
              <Marker
                key={`hydrant-${index}`}
                position={{ lat: parseFloat(hydrant.Shape_Y), lng: parseFloat(hydrant.Shape_X) }}
                icon={{
                  url: hydrantIcon,
                  scaledSize: new window.google.maps.Size(30, 30), 
                }}
                onClick={() => setSelectedHydrant(hydrant)}
              />
            ))}
            {alarms.map((alarm, index) => (
              <Marker
                key={index}
                position={{ lat: alarm.location.lat, lng: alarm.location.lng }}
                icon={{
                  url: getMarkerImage(alarm),
                  scaledSize: new window.google.maps.Size(40, 40),
                }}
                onClick={() => {
                  handleAlarmClick(alarm);
                }}
              />
            ))}
            {selectedHydrant && (
              <InfoWindow
                position={{
                  lat: parseFloat(selectedHydrant.Shape_Y),
                  lng: parseFloat(selectedHydrant.Shape_X)
                }}
                onCloseClick={() => setSelectedHydrant(null)}
              >
                <div style={{ backgroundColor: '#212c40', color: 'white', padding: '10px', borderRadius: '5px' }}>
                  <h4 style={{ margin: 0, color: 'white' }}>Hydrant Info</h4>
                  <p style={{ margin: 0 }}><strong>ID:</strong> {selectedHydrant.HYDRANT_ID}</p>
                  <p style={{ margin: 0 }}><strong>Number:</strong> {selectedHydrant.HYDRANT_NU}</p>
                  <p style={{ margin: 0 }}><strong>Coordinates:</strong> {selectedHydrant.Shape_Y}, {selectedHydrant.Shape_X}</p>
                </div>
              </InfoWindow>
            )}
            {selectedAlarm && (
              <InfoWindow
                position={{
                  lat: selectedAlarm.location.lat,
                  lng: selectedAlarm.location.lng,
                }}
                onCloseClick={() => setSelectedAlarm(null)}
              >
                <div style={{ backgroundColor: '#212c40', color: 'white', padding: '10px', borderRadius: '5px' }}>
                  <h4 style={{ margin: 0, color: 'white' }}>{selectedAlarm.CallType}</h4>
                  <p style={{ margin: 0 }}><strong>Address:</strong> {selectedAlarm.Address}</p>
                  <p style={{ margin: 0 }}><strong>Time:</strong> {new Date(selectedAlarm.TimeStamp.toDate()).toLocaleString()}</p>
                </div>
              </InfoWindow>
            )}
            {vehicles.map((vehicle) => (
              <OverlayView
                key={vehicle.id}
                position={{ lat: vehicle.latitude, lng: vehicle.longitude }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div style={{
                  position: 'relative',
                  width: '50px',
                  height: '50px'
                }} onClick={() => setSelectedVehicle(vehicle)}>
                  <div style={{
                    position: 'absolute',
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 0, 0, 0.4)',
                    transform: 'translate(-50%, -50%)',
                    animation: 'vehiclePulse 2s infinite'
                  }} />
                  <div style={{
                    position: 'absolute',
                    width: '12px',
                    height: '12px',
                    backgroundColor: '#ff0000',
                    border: '2px solid white',
                    borderRadius: '50%',
                    transform: 'translate(-50%, -50%)'
                  }} />
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    left: '15%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                    fontWeight: 'bold'
                  }}>
                    {vehicle.responderName}
                  </div>
                  <style>
                    {`
                      @keyframes vehiclePulse {
                        0% {
                          transform: translate(-50%, -50%) scale(0.5);
                          opacity: 0.8;
                        }
                        70% {
                          transform: translate(-50%, -50%) scale(2);
                          opacity: 0;
                        }
                        100% {
                          transform: translate(-50%, -50%) scale(0.5);
                          opacity: 0;
                        }
                      }
                    `}
                  </style>
                </div>
              </OverlayView>
            ))}
            {selectedVehicle && selectedVehicle.type !== "aircraft" && (
              <InfoWindow
                position={{ lat: selectedVehicle.latitude, lng: selectedVehicle.longitude }}
                onCloseClick={() => setSelectedVehicle(null)}
              >
                <div style={{ backgroundColor: '#212c40', color: 'white', padding: '10px', borderRadius: '5px' }}>
                  <h4 style={{ margin: 0, color: 'white' }}>{selectedVehicle.responderName}</h4>
                  <p style={{ margin: 0 }}><strong>Type:</strong> {selectedVehicle.vehicletype}</p>
                  <p style={{ margin: 0 }}><strong>Last Updated:</strong> {new Date(selectedVehicle.timestamp).toLocaleString()}</p>
                </div>
              </InfoWindow>
            )}
            {callerLocation && (
              <OverlayView
                position={{ lat: callerLocation.latitude, lng: callerLocation.longitude }}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              >
                <div style={{ position: 'relative' }}>
                  <div className="pulse-ring"></div>
                  <div className="center-marker"></div>
                  <div className="crosshair-horizontal"></div>
                  <div className="crosshair-vertical"></div>
                  <div className="caller-label">Caller Location</div>
                </div>
              </OverlayView>
            )}
            {(airTrafficVisible || showMedevacOnly) &&
              aircraft
                .filter(plane => !showMedevacOnly || (
                  plane.registration && 
                  medevacRegistrations.some(reg => 
                    plane.registration.toUpperCase().includes(reg.toUpperCase())
                  )
                ))
                .map((plane, index) => {
                  const isMedevac = plane.registration && 
                      medevacRegistrations.some(reg => 
                        plane.registration.toUpperCase().includes(reg.toUpperCase())
                      );
                  return isMedevac || showMedevacOnly ? (
                    // Custom overlay for MEDEVAC aircraft with animated GIF
                    <OverlayView
                      key={`aircraft-medevac-${plane.icao24}-${index}`}
                      position={{ lat: plane.latitude, lng: plane.longitude }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          transform: `translate(-50%, -50%) rotate(${plane.heading}deg)`,
                          cursor: 'pointer',
                          zIndex: 1000,
                        }}
                        onClick={() => setSelectedVehicle({
                          ...plane,
                          type: "aircraft"
                        })}
                      >
                        <img
                          src={medevacIcon} 
                          alt="MEDEVAC" 
                          style={{
                            width: '32px',
                            height: '32px',
                            filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.5))'
                          }}
                        />
                        <div
                          style={{
                            position: 'absolute',
                            top: '-25px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: 'rgba(255, 0, 0, 0.9)',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                          }}
                        >
                          {plane.callsign !== 'Unknown' ? plane.callsign : (plane.registration || plane.icao24)}
                        </div>
                      </div>
                    </OverlayView>
                  ) : (
                    // Regular aircraft marker with SVG path
                    <Marker
                      key={`aircraft-${plane.icao24}-${index}`}
                      position={{ lat: plane.latitude, lng: plane.longitude }}
                      icon={{
                        path: "M362.985,430.724l-10.248,51.234l62.332,57.969l-3.293,26.145l-71.345-23.599l-2.001,13.069l-2.057-13.529l-71.278,22.928l-5.762-23.984l64.097-59.271l-8.913-51.359l0.858-114.43l-21.945-11.338l-189.358,88.76l-1.18-32.262l213.344-180.08l0.875-107.436l51.483-9.229l0.084,107.435l213.679,178.88l-3.476,30.943l-183.226-87.393l-21.986,11.247L362.985,430.724z",
                        fillColor: getAircraftColor(plane.altitude),
                        fillOpacity: 1,
                        strokeWeight: 1,
                        strokeColor: "#ffffff",
                        scale: 0.03,
                        anchor: new window.google.maps.Point(300, 300),
                        rotation: plane.heading || 0
                      }}
                      title={null}
                      onClick={() => setSelectedVehicle({
                        ...plane,
                        type: "aircraft"
                      })}
                    >
                      {/* Aircraft label */}
                      <OverlayView
                        position={{ lat: plane.latitude, lng: plane.longitude }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        getPixelPositionOffset={(width, height) => ({
                          x: -(width / 2),
                          y: 15
                        })}
                      >
                        <div style={{
                          backgroundColor: 'rgba(0,0,0,0.5)',
                          padding: '2px 4px',
                          borderRadius: '2px',
                          color: 'white',
                          fontSize: '10px',
                          whiteSpace: 'nowrap',
                          textAlign: 'center'
                        }}>
                          {plane.callsign || plane.icao24.substring(0, 6)}
                        </div>
                      </OverlayView>
                    </Marker>
                  );
                })}
            
            {/* Aircraft InfoWindow - rendered separately to avoid OverlayView children issues */}
            {selectedVehicle && 
             selectedVehicle.type === "aircraft" && (
              <InfoWindow
                position={{ lat: selectedVehicle.latitude, lng: selectedVehicle.longitude }}
                onCloseClick={() => setSelectedVehicle(null)}
              >
                <div style={{ padding: '5px', maxWidth: '200px' }}>
                  <h3 style={{ margin: '0 0 5px 0' }}>
                    {selectedVehicle.callsign || `Aircraft ${selectedVehicle.icao24}`}
                  </h3>
                  <p style={{ margin: '2px 0', fontSize: '13px' }}>
                    ICAO24: {selectedVehicle.icao24 || 'Unknown'}
                  </p>
                  {selectedVehicle.registration && (
                    <p style={{ margin: '2px 0', fontSize: '13px' }}>
                      Registration: {selectedVehicle.registration}
                    </p>
                  )}
                  {selectedVehicle.aircraft_type && (
                    <p style={{ margin: '2px 0', fontSize: '13px' }}>
                      Aircraft Type: {selectedVehicle.aircraft_type}
                    </p>
                  )}
                  <p style={{ margin: '2px 0', fontSize: '13px' }}>
                    Country: {selectedVehicle.country || 'Unknown'}
                  </p>
                  <p style={{ margin: '2px 0', fontSize: '13px' }}>
                    Altitude: {selectedVehicle.altitude ? `${Math.round(selectedVehicle.altitude)} ft` : 'Unknown'}
                  </p>
                  <p style={{ margin: '2px 0', fontSize: '13px' }}>
                    Speed: {selectedVehicle.velocity ? `${Math.round(selectedVehicle.velocity)} kts` : 'Unknown'}
                  </p>
                  <p style={{ margin: '2px 0', fontSize: '13px' }}>
                    Heading: {selectedVehicle.heading ? `${Math.round(selectedVehicle.heading)}°` : 'Unknown'}
                  </p>
                  <p style={{ margin: '2px 0', fontSize: '13px' }}>
                    On Ground: {selectedVehicle.onGround ? 'Yes' : 'No'}
                  </p>
                </div>
              </InfoWindow>
            )}
            
            {trafficLayerVisible && <TrafficLayer />}
          </GoogleMap>
        </LoadScript>
      </div>
    );
  };
  
  export default FullScreenMap;
